// src/components/Profile/Profile.tsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Divider,
  Button,
  TextField,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Card,
  CardContent,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  InputAdornment,
  IconButton,
  Switch,
  FormControlLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  NativeSelect,
} from '@mui/material';
import {
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Business as BusinessIcon,
  Smartphone as SmartphoneIcon,
  Devices as DevicesIcon,
  Lock as LockIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  LocationOn as LocationIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Sms as SmsIcon,
  Security as SecurityIcon,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import GooglePlacesAutocompleteNative from '../Common/GooglePlacesAutocompleteNative';

interface ProfileData {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  company_name: string;
  extension: string | null;
  phone: string | null;
  office_address?: string | null;
  user_type: string;
  softphone_enabled: boolean;
  softphone_username?: string;
  softphone_state?: number;
  softphone_devices?: SoftphoneDevice[];
  phone_number?: string | null;
  default_sms_number?: string | null;
  sms_enabled?: boolean;
  available_direct_numbers?: string[];
  available_shared_numbers?: Array<{
    number: string;
    description?: string;
  }>;
  timezone?: string;
  two_fa_enabled?: boolean;
  two_fa_method?: 'email' | 'sms' | 'both';
  two_fa_phone?: string | null;
  two_fa_email?: string | null;
}

interface SoftphoneDevice {
  device_id: string;
  device_name: string;
  last_seen: string;
  is_active: boolean;
}

const Profile: React.FC = () => {
  const { member } = useAuth();
  const [loading, setLoading] = useState(true);
  const [profileData, setProfileData] = useState<ProfileData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: '',
  });
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [passwordSuccess, setPasswordSuccess] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [editingOfficeAddress, setEditingOfficeAddress] = useState(false);
  const [officeAddress, setOfficeAddress] = useState('');
  const [savingProfile, setSavingProfile] = useState(false);
  const [editingSmsSettings, setEditingSmsSettings] = useState(false);
  const [smsEnabled, setSmsEnabled] = useState(false);
  const [selectedSmsNumber, setSelectedSmsNumber] = useState<string>('');
  const [savingSmsSettings, setSavingSmsSettings] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [timezone, setTimezone] = useState('America/Toronto');
  const [editingTimezone, setEditingTimezone] = useState(false);
  const [editing2FA, setEditing2FA] = useState(false);
  const [twoFaMethod, setTwoFaMethod] = useState<'email' | 'sms' | 'both'>('email');
  const [twoFaPhone, setTwoFaPhone] = useState('');
  const [twoFaEmail, setTwoFaEmail] = useState('');
  const [saving2FA, setSaving2FA] = useState(false);

  useEffect(() => {
    fetchProfileData();
  }, []);

  useEffect(() => {
    if (profileData?.office_address) {
      setOfficeAddress(profileData.office_address);
    }
    if (profileData?.sms_enabled !== undefined) {
      setSmsEnabled(profileData.sms_enabled);
    }
    if (profileData?.default_sms_number) {
      console.log('Setting default SMS number from profile:', profileData.default_sms_number);
      setSelectedSmsNumber(profileData.default_sms_number);
    }
    if (profileData?.phone_number) {
      setPhoneNumber(profileData.phone_number);
    }
    if (profileData?.timezone) {
      setTimezone(profileData.timezone);
    }
    if (profileData?.two_fa_method) {
      setTwoFaMethod(profileData.two_fa_method);
    }
    if (profileData?.two_fa_phone) {
      setTwoFaPhone(profileData.two_fa_phone);
    }
    if (profileData?.two_fa_email) {
      setTwoFaEmail(profileData.two_fa_email);
    }
    
    // Log available numbers
    console.log('Available direct numbers:', profileData?.available_direct_numbers);
    console.log('Available shared numbers:', profileData?.available_shared_numbers);
  }, [profileData]);

  const fetchProfileData = async () => {
    try {
      setLoading(true);
      const response = await api.get('/endpoints/profile');
      if (response.data.success) {
        setProfileData(response.data.data);
      }
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load profile');
      console.error('Profile error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveOfficeAddress = async () => {
    try {
      setSavingProfile(true);
      const response = await api.post('/endpoints/update_profile', {
        office_address: officeAddress
      });
      
      if (response.data.success) {
        setEditingOfficeAddress(false);
        if (profileData) {
          setProfileData({ ...profileData, office_address: officeAddress });
        }
        setError(null);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update office address');
    } finally {
      setSavingProfile(false);
    }
  };

  const handleSaveSmsSettings = async () => {
    try {
      setSavingSmsSettings(true);
      
      const payload = {
        sms_enabled: smsEnabled ? 1 : 0,
        default_sms_number: smsEnabled ? selectedSmsNumber : null,
        phone_number: phoneNumber
      };
      
      console.log('Saving SMS settings with payload:', payload);
      
      const response = await api.post('/endpoints/update_profile', payload);
      
      console.log('Save response:', response);
      
      if (response.data.success) {
        setEditingSmsSettings(false);
        if (profileData) {
          setProfileData({ 
            ...profileData, 
            sms_enabled: smsEnabled,
            default_sms_number: smsEnabled ? selectedSmsNumber : null,
            phone_number: phoneNumber
          });
        }
        setError(null);
        // Show success message
        console.log('SMS settings saved successfully!');
        alert('SMS settings saved successfully!');
      }
    } catch (err: any) {
      const errorMsg = err.response?.data?.message || err.message || 'Failed to update SMS settings';
      setError(errorMsg);
      console.error('Error saving SMS settings:', err);
      console.error('Error response:', err.response);
      alert('Error: ' + errorMsg);
    } finally {
      setSavingSmsSettings(false);
    }
  };

  const handleSave2FA = async () => {
    try {
      setSaving2FA(true);
      const response = await api.post('/auth/two-factor', {
        method: twoFaMethod,
        phone: twoFaMethod === 'sms' ? twoFaPhone : undefined,
        email: twoFaEmail || undefined,
      }, {
        params: { action: 'setup' }
      });
      
      if (response.data.success) {
        setEditing2FA(false);
        await fetchProfileData();
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update 2FA settings');
    } finally {
      setSaving2FA(false);
    }
  };

  const handleSaveTimezone = async () => {
    try {
      setSavingProfile(true);
      
      const response = await api.post('/endpoints/update_profile', {
        timezone: timezone
      });
      
      if (response.data.success) {
        setEditingTimezone(false);
        if (profileData) {
          setProfileData({ 
            ...profileData, 
            timezone: timezone
          });
        }
      } else {
        setError(response.data.message || 'Failed to update timezone');
      }
    } catch (err) {
      setError('Failed to update timezone');
    } finally {
      setSavingProfile(false);
    }
  };

  const handlePasswordChange = async () => {
    setPasswordError(null);
    setPasswordSuccess(false);

    // Validate passwords
    if (!passwordData.current_password) {
      setPasswordError('Current password is required');
      return;
    }

    if (!passwordData.new_password) {
      setPasswordError('New password is required');
      return;
    }

    if (passwordData.new_password.length < 8) {
      setPasswordError('New password must be at least 8 characters long');
      return;
    }

    if (passwordData.new_password !== passwordData.confirm_password) {
      setPasswordError('New passwords do not match');
      return;
    }

    try {
      setSavingPassword(true);
      const response = await api.post('/endpoints/change_password', {
        current_password: passwordData.current_password,
        new_password: passwordData.new_password,
      });

      if (response.data.success) {
        setPasswordSuccess(true);
        setTimeout(() => {
          setPasswordDialogOpen(false);
          setPasswordData({
            current_password: '',
            new_password: '',
            confirm_password: '',
          });
          setPasswordSuccess(false);
        }, 2000);
      }
    } catch (err: any) {
      setPasswordError(err.response?.data?.message || 'Failed to change password');
    } finally {
      setSavingPassword(false);
    }
  };

  const getSoftphoneStateChip = (state?: number) => {
    switch (state) {
      case -1:
        return <Chip label="Unavailable" color="default" size="small" icon={<CancelIcon />} />;
      case 0:
        return <Chip label="Offline" color="default" size="small" icon={<CancelIcon />} />;
      case 1:
        return <Chip label="Online" color="success" size="small" icon={<CheckCircleIcon />} />;
      case 2:
        return <Chip label="Away" color="warning" size="small" icon={<CheckCircleIcon />} />;
      case 3:
        return <Chip label="Busy (DND)" color="error" size="small" icon={<CancelIcon />} />;
      case 4:
        return <Chip label="At The Desk" color="info" size="small" icon={<CheckCircleIcon />} />;
      case 5:
        return <Chip label="Available on PBX" color="success" size="small" icon={<CheckCircleIcon />} />;
      default:
        return <Chip label="Unknown" color="default" size="small" />;
    }
  };

  const formatUserType = (type: string) => {
    return type.split('_').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        {error}
      </Alert>
    );
  }

  if (!profileData) {
    return null;
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        My Profile
      </Typography>

      {/* User Information Card */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
            <PersonIcon sx={{ mr: 1 }} />
            User Information
          </Typography>
          <Divider sx={{ mb: 2 }} />
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <EmailIcon sx={{ mr: 2, color: 'text.secondary' }} />
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Email
                  </Typography>
                  <Typography variant="body1">
                    {profileData.email}
                  </Typography>
                </Box>
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <PersonIcon sx={{ mr: 2, color: 'text.secondary' }} />
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Name
                  </Typography>
                  <Typography variant="body1">
                    {profileData.first_name} {profileData.last_name}
                  </Typography>
                </Box>
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <BusinessIcon sx={{ mr: 2, color: 'text.secondary' }} />
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Company
                  </Typography>
                  <Typography variant="body1">
                    {profileData.company_name}
                  </Typography>
                </Box>
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <PhoneIcon sx={{ mr: 2, color: 'text.secondary' }} />
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Extension
                  </Typography>
                  <Typography variant="body1">
                    {profileData.extension || 'Not assigned'}
                  </Typography>
                </Box>
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <PersonIcon sx={{ mr: 2, color: 'text.secondary' }} />
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Role
                  </Typography>
                  <Typography variant="body1">
                    {formatUserType(profileData.user_type)}
                  </Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Office Address Card */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <LocationIcon sx={{ mr: 1 }} />
              Office Address
            </Box>
            {!editingOfficeAddress && (
              <IconButton onClick={() => setEditingOfficeAddress(true)} size="small">
                <EditIcon />
              </IconButton>
            )}
          </Typography>
          <Divider sx={{ mb: 2 }} />
          
          {editingOfficeAddress ? (
            <Box>
              <GooglePlacesAutocompleteNative
                value={officeAddress}
                onChange={setOfficeAddress}
                onPlaceSelected={(place) => {
                  if (place.formatted_address) {
                    setOfficeAddress(place.formatted_address);
                  }
                }}
                label="Office Address"
                placeholder="Enter your office address..."
                fullWidth
              />
              <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                <Button
                  variant="contained"
                  startIcon={<SaveIcon />}
                  onClick={handleSaveOfficeAddress}
                  disabled={savingProfile}
                >
                  {savingProfile ? 'Saving...' : 'Save'}
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => {
                    setEditingOfficeAddress(false);
                    setOfficeAddress(profileData?.office_address || '');
                  }}
                  disabled={savingProfile}
                >
                  Cancel
                </Button>
              </Box>
            </Box>
          ) : (
            <Typography variant="body1">
              {profileData?.office_address || 'No office address set'}
            </Typography>
          )}
          
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
            This address is used to calculate travel time for out-of-office meetings
          </Typography>
        </CardContent>
      </Card>

      {/* Timezone Settings Card */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <LocationIcon sx={{ mr: 1 }} />
              Timezone
            </Box>
            {!editingTimezone && (
              <IconButton onClick={() => setEditingTimezone(true)} size="small">
                <EditIcon />
              </IconButton>
            )}
          </Typography>
          <Divider sx={{ mb: 2 }} />
          
          {editingTimezone ? (
            <Box>
              <FormControl fullWidth>
                <InputLabel>Select Timezone</InputLabel>
                <Select
                  value={timezone}
                  onChange={(e) => setTimezone(e.target.value)}
                  label="Select Timezone"
                >
                  <MenuItem value="America/Vancouver">Pacific Time (BC)</MenuItem>
                  <MenuItem value="America/Edmonton">Mountain Time (AB)</MenuItem>
                  <MenuItem value="America/Regina">Central Time (SK - no DST)</MenuItem>
                  <MenuItem value="America/Winnipeg">Central Time (MB)</MenuItem>
                  <MenuItem value="America/Toronto">Eastern Time (ON/QC)</MenuItem>
                  <MenuItem value="America/Halifax">Atlantic Time (NS/NB/PE)</MenuItem>
                  <MenuItem value="America/St_Johns">Newfoundland Time (NL)</MenuItem>
                </Select>
              </FormControl>
              <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                <Button
                  variant="contained"
                  startIcon={<SaveIcon />}
                  onClick={handleSaveTimezone}
                  disabled={savingProfile}
                >
                  {savingProfile ? 'Saving...' : 'Save'}
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => {
                    setEditingTimezone(false);
                    setTimezone(profileData?.timezone || 'America/Toronto');
                  }}
                  disabled={savingProfile}
                >
                  Cancel
                </Button>
              </Box>
            </Box>
          ) : (
            <Box>
              <Typography variant="body1">
                {timezone === 'America/Vancouver' && 'Pacific Time (BC)'}
                {timezone === 'America/Edmonton' && 'Mountain Time (AB)'}
                {timezone === 'America/Regina' && 'Central Time (SK - no DST)'}
                {timezone === 'America/Winnipeg' && 'Central Time (MB)'}
                {timezone === 'America/Toronto' && 'Eastern Time (ON/QC)'}
                {timezone === 'America/Halifax' && 'Atlantic Time (NS/NB/PE)'}
                {timezone === 'America/St_Johns' && 'Newfoundland Time (NL)'}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                {new Date().toLocaleString('en-CA', { timeZone: timezone, timeZoneName: 'short' })}
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* SMS Notification Settings Card */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <SmsIcon sx={{ mr: 1 }} />
              SMS Travel Notifications
            </Box>
            {!editingSmsSettings && (
              <IconButton onClick={() => setEditingSmsSettings(true)} size="small">
                <EditIcon />
              </IconButton>
            )}
          </Typography>
          <Divider sx={{ mb: 2 }} />
          
          {editingSmsSettings ? (
            <Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={smsEnabled}
                    onChange={(e) => setSmsEnabled(e.target.checked)}
                    name="smsEnabled"
                  />
                }
                label="Enable SMS travel time notifications"
                sx={{ mb: 2 }}
              />
              
              {smsEnabled && (
                <>
                  <TextField
                    fullWidth
                    size="small"
                    label="Your Cell Phone Number"
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                    placeholder="(289) 314-7818"
                    helperText="Number where you'll receive SMS notifications"
                    sx={{ mb: 2 }}
                  />
                  <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                    <InputLabel htmlFor="sms-number-native">Outgoing SMS Number</InputLabel>
                    <NativeSelect
                      inputProps={{
                        name: 'smsNumber',
                        id: 'sms-number-native',
                      }}
                      value={selectedSmsNumber || ''}
                      onChange={(e) => {
                        const newValue = e.target.value;
                        console.log('Dropdown onChange fired:', newValue);
                        setSelectedSmsNumber(newValue);
                      }}
                    >
                      <option value="">Select a number</option>
                      {profileData?.available_direct_numbers && profileData.available_direct_numbers.length > 0 && (
                        <optgroup label="Direct Numbers">
                          {profileData.available_direct_numbers.map((number) => (
                            <option key={`direct-${number}`} value={number}>
                              {number.replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3')}
                            </option>
                          ))}
                        </optgroup>
                      )}
                      {profileData?.available_shared_numbers && profileData.available_shared_numbers.length > 0 && (
                        <optgroup label="Shared Numbers">
                          {profileData.available_shared_numbers.map((shared) => (
                            <option key={`shared-${shared.number}`} value={shared.number}>
                              {shared.number.replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3')}
                              {shared.description && ` - ${shared.description}`}
                            </option>
                          ))}
                        </optgroup>
                      )}
                    </NativeSelect>
                  </FormControl>
                </>
              )}
              
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="contained"
                  startIcon={<SaveIcon />}
                  onClick={handleSaveSmsSettings}
                  disabled={savingSmsSettings || (smsEnabled && !selectedSmsNumber)}
                >
                  {savingSmsSettings ? 'Saving...' : 'Save'}
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => {
                    setEditingSmsSettings(false);
                    setSmsEnabled(profileData?.sms_enabled || false);
                    setSelectedSmsNumber(profileData?.default_sms_number || '');
                    setPhoneNumber(profileData?.phone_number || '');
                  }}
                  disabled={savingSmsSettings}
                >
                  Cancel
                </Button>
              </Box>
            </Box>
          ) : (
            <Box>
              <Typography variant="body1" sx={{ mb: 1 }}>
                Status: {profileData?.sms_enabled ? 'Enabled' : 'Disabled'}
              </Typography>
              {profileData?.sms_enabled && profileData?.default_sms_number && (
                <Typography variant="body1">
                  Outgoing Number: {profileData.default_sms_number.replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3')}
                </Typography>
              )}
            </Box>
          )}
          
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
            When enabled, you'll receive SMS reminders about when to leave for appointments based on current traffic conditions
          </Typography>
        </CardContent>
      </Card>

      {/* Two-Factor Authentication Card */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <SecurityIcon sx={{ mr: 1 }} />
              Two-Factor Authentication
            </Box>
            {!editing2FA && (
              <IconButton onClick={() => setEditing2FA(true)} size="small">
                <EditIcon />
              </IconButton>
            )}
          </Typography>
          <Divider sx={{ mb: 2 }} />
          
          {editing2FA ? (
            <Box>
              <Alert severity="info" sx={{ mb: 2 }}>
                Two-factor authentication adds an extra layer of security to your account
              </Alert>
              
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Verification Method</InputLabel>
                <Select
                  value={twoFaMethod}
                  onChange={(e) => setTwoFaMethod(e.target.value as 'email' | 'sms' | 'both')}
                  label="Verification Method"
                >
                  <MenuItem value="email">Email</MenuItem>
                  <MenuItem value="sms">SMS Text Message</MenuItem>
                </Select>
              </FormControl>
              
              {twoFaMethod === 'sms' && (
                <TextField
                  fullWidth
                  label="Phone Number for 2FA"
                  value={twoFaPhone}
                  onChange={(e) => setTwoFaPhone(e.target.value)}
                  placeholder="+1 (555) 123-4567"
                  helperText="Enter your mobile phone number to receive verification codes"
                  sx={{ mb: 2 }}
                />
              )}
              
              {twoFaMethod === 'email' && (
                <TextField
                  fullWidth
                  label="Alternative Email (Optional)"
                  value={twoFaEmail}
                  onChange={(e) => setTwoFaEmail(e.target.value)}
                  placeholder="backup@example.com"
                  helperText="Leave blank to use your primary email address"
                  sx={{ mb: 2 }}
                />
              )}
              
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="contained"
                  startIcon={<SaveIcon />}
                  onClick={handleSave2FA}
                  disabled={saving2FA}
                >
                  {saving2FA ? 'Saving...' : 'Save'}
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => {
                    setEditing2FA(false);
                    setTwoFaMethod(profileData?.two_fa_method || 'email');
                    setTwoFaPhone(profileData?.two_fa_phone || '');
                    setTwoFaEmail(profileData?.two_fa_email || '');
                  }}
                  disabled={saving2FA}
                >
                  Cancel
                </Button>
              </Box>
            </Box>
          ) : (
            <Box>
              <Chip
                icon={profileData?.two_fa_enabled ? <CheckCircleIcon /> : <CancelIcon />}
                label={profileData?.two_fa_enabled ? 'Enabled' : 'Required'}
                color={profileData?.two_fa_enabled ? 'success' : 'warning'}
                sx={{ mb: 2 }}
              />
              
              {profileData?.two_fa_enabled ? (
                <>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>Method:</strong> {profileData.two_fa_method === 'sms' ? 'SMS Text Message' : 'Email'}
                  </Typography>
                  {profileData.two_fa_method === 'sms' && profileData.two_fa_phone && (
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>Phone:</strong> {profileData.two_fa_phone}
                    </Typography>
                  )}
                  {profileData.two_fa_method === 'email' && profileData.two_fa_email && (
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>Email:</strong> {profileData.two_fa_email}
                    </Typography>
                  )}
                </>
              ) : (
                <Alert severity="warning" sx={{ mt: 1 }}>
                  Two-factor authentication is required for all accounts. Please set it up to secure your account.
                </Alert>
              )}
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Softphone Information Card - Only show if softphone_enabled */}
      {profileData.softphone_enabled && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
              <SmartphoneIcon sx={{ mr: 1 }} />
              Softphone Information
            </Typography>
            <Divider sx={{ mb: 2 }} />
            
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mr: 2 }}>
                    Status:
                  </Typography>
                  {getSoftphoneStateChip(profileData.softphone_state)}
                </Box>
              </Grid>

              {profileData.extension && (
                <Grid item xs={12} md={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mr: 2 }}>
                      Extension:
                    </Typography>
                    <Typography variant="body1">
                      {profileData.extension}
                    </Typography>
                  </Box>
                </Grid>
              )}
            </Grid>

            {/* Softphone Devices */}
            {profileData.softphone_devices && profileData.softphone_devices.length > 0 && (
              <>
                <Typography variant="subtitle1" sx={{ mt: 3, mb: 2, display: 'flex', alignItems: 'center' }}>
                  <DevicesIcon sx={{ mr: 1 }} />
                  Registered Devices
                </Typography>
                <List>
                  {profileData.softphone_devices.map((device) => (
                    <ListItem key={device.device_id} sx={{ pl: 0 }}>
                      <ListItemIcon>
                        <SmartphoneIcon color={device.is_active ? 'success' : 'disabled'} />
                      </ListItemIcon>
                      <ListItemText
                        primary={device.device_name}
                        secondary={`Last seen: ${new Date(device.last_seen).toLocaleString()}`}
                      />
                      {device.is_active && (
                        <Chip label="Active" color="success" size="small" />
                      )}
                    </ListItem>
                  ))}
                </List>
              </>
            )}
          </CardContent>
        </Card>
      )}

      {/* Security Card */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
            <LockIcon sx={{ mr: 1 }} />
            Security
          </Typography>
          <Divider sx={{ mb: 2 }} />
          
          <Button
            variant="outlined"
            startIcon={<LockIcon />}
            onClick={() => setPasswordDialogOpen(true)}
          >
            Change Password
          </Button>
        </CardContent>
      </Card>

      {/* Change Password Dialog */}
      <Dialog open={passwordDialogOpen} onClose={() => setPasswordDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Change Password</DialogTitle>
        <DialogContent>
          {passwordError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {passwordError}
            </Alert>
          )}
          {passwordSuccess && (
            <Alert severity="success" sx={{ mb: 2 }}>
              Password changed successfully!
            </Alert>
          )}
          
          <TextField
            fullWidth
            label="Current Password"
            type={showCurrentPassword ? 'text' : 'password'}
            value={passwordData.current_password}
            onChange={(e) => setPasswordData({ ...passwordData, current_password: e.target.value })}
            margin="normal"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    edge="end"
                  >
                    {showCurrentPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          
          <TextField
            fullWidth
            label="New Password"
            type={showNewPassword ? 'text' : 'password'}
            value={passwordData.new_password}
            onChange={(e) => setPasswordData({ ...passwordData, new_password: e.target.value })}
            margin="normal"
            helperText="Must be at least 8 characters long"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    edge="end"
                  >
                    {showNewPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          
          <TextField
            fullWidth
            label="Confirm New Password"
            type={showConfirmPassword ? 'text' : 'password'}
            value={passwordData.confirm_password}
            onChange={(e) => setPasswordData({ ...passwordData, confirm_password: e.target.value })}
            margin="normal"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    edge="end"
                  >
                    {showConfirmPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPasswordDialogOpen(false)} disabled={savingPassword}>
            Cancel
          </Button>
          <Button
            onClick={handlePasswordChange}
            variant="contained"
            disabled={savingPassword}
            startIcon={savingPassword ? <CircularProgress size={20} /> : <LockIcon />}
          >
            Change Password
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Profile;